import React, { useState, useEffect } from 'react';
import {
    Box,
    Button,
    Container,
    Heading,
    Text,
    Stack,
} from '@chakra-ui/react';
import { Link } from "react-router-dom";
import { useAuth } from './authprovider';
import { extractErrorMessage } from '../utils/errorHandler';
import axiosInstance from '../utils/axiosConfig';
import { ContentCard } from '../Components/ContentCard';
import StyledInput from '../Components/ui/styled-input';

interface AccountDetailsProps {
  userId: string | null;
}

interface UserDetails {
  email: string;
  name: string;
  availableTokens: number;
}

export function AccountDetails({ userId }: AccountDetailsProps) {
    const { userEmail, isLoggedIn, userRole, isDebug, allowedAdminEmails } = useAuth();
    const checkIfLoggedIn = useAuth().checkIfLoggedIn;
    const [email, setEmail] = useState<string>('');
    const [name, setName] = useState<string>('');
    const [token, setToken] = useState<number>(0);

    const [redeemCode, setRedeemCode] = useState<string>('');
    const [redeemMessage, setRedeemMessage] = useState<string>('');
    const [redeemMessageType, setRedeemMessageType] = useState<'success' | 'error' | ''>('');
    const [isRedeeming, setIsRedeeming] = useState<boolean>(false);

    const [developerAccessEnabled, setDeveloperAccessEnabled] = useState<boolean>(false);
    const [developerMessage, setDeveloperMessage] = useState<string>('');
    const [developerMessageType, setDeveloperMessageType] = useState<'success' | 'error' | ''>('');
    const [isEnablingDeveloperAccess, setIsEnablingDeveloperAccess] = useState<boolean>(false);
    
    // Form states
    const [newName, setNewName] = useState<string>('');
    const [newEmail, setNewEmail] = useState<string>('');
    const [emailPassword, setEmailPassword] = useState<string>('');
    const [currentPassword, setCurrentPassword] = useState<string>('');
    const [newPassword, setNewPassword] = useState<string>('');
    const [confirmPassword, setConfirmPassword] = useState<string>('');
    
    // Loading states
    const [isUpdatingName, setIsUpdatingName] = useState<boolean>(false);
    const [isUpdatingEmail, setIsUpdatingEmail] = useState<boolean>(false);
    const [isUpdatingPassword, setIsUpdatingPassword] = useState<boolean>(false);
    const [isUpgradingToDeveloper, setIsUpgradingToDeveloper] = useState<boolean>(false);

    // Admin credit states
    const [adminTargetUser, setAdminTargetUser] = useState<string>('');
    const [adminAmount, setAdminAmount] = useState<number>(0);
    const [adminDescription, setAdminDescription] = useState<string>('');
    const [adminMessage, setAdminMessage] = useState<string>('');
    const [adminMessageType, setAdminMessageType] = useState<'success' | 'error' | ''>('');
    const [isAdminCrediting, setIsAdminCrediting] = useState<boolean>(false);
    
    // Status messages
    const [nameMessage, setNameMessage] = useState<string>('');
    const [emailMessage, setEmailMessage] = useState<string>('');
    const [passwordMessage, setPasswordMessage] = useState<string>('');
    
    // Message types (success or error)
    const [nameMessageType, setNameMessageType] = useState<'success' | 'error' | ''>('');
    const [emailMessageType, setEmailMessageType] = useState<'success' | 'error' | ''>('');
    const [passwordMessageType, setPasswordMessageType] = useState<'success' | 'error' | ''>();
    const [upgradeMessage, setUpgradeMessage] = useState<string>('');
    const [upgradeMessageType, setUpgradeMessageType] = useState<'success' | 'error' | ''>('');

    const handleUpgradeToDeveloper = async () => {
        setUpgradeMessage('');
        setUpgradeMessageType('');
        setIsUpgradingToDeveloper(true);

        try {
            await axiosInstance.post('/upgradeToDeveloper');
            await checkIfLoggedIn();
            setUpgradeMessage('Developer tools have been enabled for your account.');
            setUpgradeMessageType('success');
        } catch (error) {
            const errorMessage = extractErrorMessage(error);
            setUpgradeMessage(`Failed to upgrade: ${errorMessage}`);
            setUpgradeMessageType('error');
        } finally {
            setIsUpgradingToDeveloper(false);
        }
    };

    // Fetch user details
    useEffect(() => {
        // Create an async function inside useEffect
        const checkAuthAndFetchDetails = async () => {
            // Only fetch if user is logged in
            // Short-circuit: if isLoggedIn is false, try to check login status
            if (!isLoggedIn && !(await checkIfLoggedIn())) {
                return;
            }
            
            // Fetch user details if authenticated
            try {
                // Fetch user details
                const userResponse = await axiosInstance.get<{ email: string; name: string }>(`/userDetails`);
                console.log(userResponse.data);
                const { email, name } = userResponse.data;
                setEmail(email);
                setName(name);
                setNewName(name); // Initialize form with current name
                setNewEmail(email); // Initialize form with current email
                
                // Fetch token balance from new endpoint
                const tokenResponse = await axiosInstance.get<{ userId: string; balance: number }>(`/api/tokens/balance`);
                setToken(tokenResponse.data.balance);

                try {
                    const statusResp = await axiosInstance.get<{ code: string; redeemed: boolean }>(`/api/tokens/grant-status?code=DEV_ACCESS_10K`);
                    setDeveloperAccessEnabled(statusResp.data.redeemed);
                } catch {
                }

                const pendingCoupon = sessionStorage.getItem('pending_coupon');
                if (pendingCoupon) {
                    sessionStorage.removeItem('pending_coupon');
                    try {
                        await axiosInstance.post('/api/tokens/redeem', { code: pendingCoupon, source: 'signup' });
                        const updated = await axiosInstance.get<{ userId: string; balance: number }>(`/api/tokens/balance`);
                        setToken(updated.data.balance);
                    } catch {
                    }
                }
            } catch (error) {
                console.error('Failed to fetch user details:', error);
            }
        };

        // Execute the async function
        checkAuthAndFetchDetails();
    }, [userId, isLoggedIn, checkIfLoggedIn]);

    const refreshBalance = async () => {
        const tokenResponse = await axiosInstance.get<{ userId: string; balance: number }>(`/api/tokens/balance`);
        setToken(tokenResponse.data.balance);
    };

    const handleRedeemCoupon = async () => {
        const code = redeemCode.trim();
        if (!code) {
            setRedeemMessage('Please enter a coupon code');
            setRedeemMessageType('error');
            return;
        }

        setIsRedeeming(true);
        setRedeemMessage('');
        setRedeemMessageType('');

        try {
            await axiosInstance.post('/api/tokens/redeem', { code, source: 'my_account' });
            await refreshBalance();
            setRedeemCode('');
            setRedeemMessage('Coupon redeemed successfully.');
            setRedeemMessageType('success');
        } catch (error) {
            const errorMessage = extractErrorMessage(error);
            setRedeemMessage(`Failed to redeem coupon: ${errorMessage}`);
            setRedeemMessageType('error');
        } finally {
            setIsRedeeming(false);
        }
    };

    const handleEnableDeveloperAccess = async () => {
        setIsEnablingDeveloperAccess(true);
        setDeveloperMessage('');
        setDeveloperMessageType('');

        try {
            await axiosInstance.post('/api/tokens/redeem', { code: 'DEV_ACCESS_10K', source: 'developer_access' });
            await refreshBalance();
            setDeveloperAccessEnabled(true);
            setDeveloperMessage('Developer access enabled.');
            setDeveloperMessageType('success');
        } catch (error) {
            const errorMessage = extractErrorMessage(error);
            setDeveloperMessage(`Failed to enable developer access: ${errorMessage}`);
            setDeveloperMessageType('error');
        } finally {
            setIsEnablingDeveloperAccess(false);
        }
    };

    const handleUpdateName = async () => {
        if (!newName.trim()) {
            setNameMessage('Name cannot be empty');
            setNameMessageType('error');
            return;
        }
        
        setNameMessage('');
        setNameMessageType('');
        setIsUpdatingName(true);
        
        try {
            await axiosInstance.post('/updateName', { name: newName });
            setName(newName);
            setNameMessage('Your name has been successfully updated.');
            setNameMessageType('success');
        } catch (error) {
            console.error('Failed to update name:', error);
            const errorMessage = extractErrorMessage(error);
            setNameMessage(`Failed to update name: ${errorMessage}`);
            setNameMessageType('error');
        } finally {
            setIsUpdatingName(false);
        }
    };
    
    const handleUpdateEmail = async () => {
        if (!newEmail.trim()) {
            setEmailMessage('Email cannot be empty');
            setEmailMessageType('error');
            return;
        }
        
        if (!emailPassword) {
            setEmailMessage('Password is required to update email');
            setEmailMessageType('error');
            return;
        }
        
        setEmailMessage('');
        setEmailMessageType('');
        setIsUpdatingEmail(true);
        
        try {
            await axiosInstance.post('/updateEmail', { 
                email: newEmail,
                password: emailPassword 
            });
            setEmail(newEmail);
            setEmailPassword('');
            setEmailMessage('Your email has been successfully updated.');
            setEmailMessageType('success');
        } catch (error) {
            console.error('Failed to update email:', error);
            const errorMessage = extractErrorMessage(error);
            setEmailMessage(`Failed to update email: ${errorMessage}`);
            setEmailMessageType('error');
        } finally {
            setIsUpdatingEmail(false);
        }
    };
    
    const handleUpdatePassword = async () => {
        if (!currentPassword) {
            setPasswordMessage('Current password is required');
            setPasswordMessageType('error');
            return;
        }
        
        if (!newPassword) {
            setPasswordMessage('New password cannot be empty');
            setPasswordMessageType('error');
            return;
        }
        
        if (newPassword !== confirmPassword) {
            setPasswordMessage('New passwords do not match');
            setPasswordMessageType('error');
            return;
        }
        
        setPasswordMessage('');
        setPasswordMessageType('');
        setIsUpdatingPassword(true);
        
        try {
            await axiosInstance.post('/updatePassword', { 
                currentPassword,
                newPassword 
            });
            setCurrentPassword('');
            setNewPassword('');
            setConfirmPassword('');
            setPasswordMessage('Your password has been successfully updated.');
            setPasswordMessageType('success');
        } catch (error) {
            console.error('Failed to update password:', error);
            const errorMessage = extractErrorMessage(error);
            setPasswordMessage(`Failed to update password: ${errorMessage}`);
            setPasswordMessageType('error');
        } finally {
            setIsUpdatingPassword(false);
        }
    };

    const handleAdminCredit = async () => {
        const target = adminTargetUser.trim();
        if (!target) {
            setAdminMessage('Please enter a target user email or UUID');
            setAdminMessageType('error');
            return;
        }
        if (adminAmount <= 0) {
            setAdminMessage('Amount must be greater than 0');
            setAdminMessageType('error');
            return;
        }

        setIsAdminCrediting(true);
        setAdminMessage('');
        setAdminMessageType('');

        try {
            const isUUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(target);
            const payload: Record<string, unknown> = { amount: adminAmount };
            if (isUUID) {
                payload.targetUserId = target;
            } else {
                payload.targetUserEmail = target;
            }
            if (adminDescription.trim()) {
                payload.description = adminDescription.trim();
            }

            const response = await axiosInstance.post<{ status: string; userId: string; credited: number; newBalance: number }>('/api/tokens/admin/credit', payload);
            setAdminMessage(`Successfully credited ${response.data.credited} tokens. New balance: ${response.data.newBalance}`);
            setAdminMessageType('success');
            setAdminTargetUser('');
            setAdminAmount(0);
            setAdminDescription('');
        } catch (error) {
            const errorMessage = extractErrorMessage(error);
            setAdminMessage(`Failed to credit tokens: ${errorMessage}`);
            setAdminMessageType('error');
        } finally {
            setIsAdminCrediting(false);
        }
    };

    const isAdminAllowed = isDebug && allowedAdminEmails.includes(userEmail);

    return (
        <Container maxW="6xl" py={{ base: 6, md: 10 }}>
            <Heading as="h1" size="2xl" fontWeight="bold" lineHeight="1.1" mb={6} color="gray.900">Account</Heading>

            <Stack gap={6}>
                {isAdminAllowed && (
                    <ContentCard>
                        <Heading size="md" mb={4} color="gray.900">Admin: Credit Tokens</Heading>
                        <Stack gap={4}>
                            <Box>
                                <Text mb={2} color="gray.900">Target User (Email or UUID)</Text>
                                <StyledInput value={adminTargetUser} onChange={(e) => setAdminTargetUser(e.target.value)} placeholder="user@example.com or uuid" />
                            </Box>
                            <Box>
                                <Text mb={2} color="gray.900">Amount</Text>
                                <StyledInput type="number" value={adminAmount || ''} onChange={(e) => setAdminAmount(parseInt(e.target.value) || 0)} placeholder="Number of tokens" />
                            </Box>
                            <Box>
                                <Text mb={2} color="gray.900">Description (optional)</Text>
                                <StyledInput value={adminDescription} onChange={(e) => setAdminDescription(e.target.value)} placeholder="Reason for credit" />
                            </Box>
                            {adminMessage && (
                                <Text color={adminMessageType === 'success' ? 'green.600' : 'red.600'}>
                                    {adminMessage}
                                </Text>
                            )}
                            <Button colorScheme="blue" size="lg" fontWeight="semibold" borderRadius="md" onClick={handleAdminCredit} isDisabled={isAdminCrediting} width="full">
                                {isAdminCrediting ? 'Crediting...' : 'Credit Tokens'}
                            </Button>
                        </Stack>
                    </ContentCard>
                )}
                <ContentCard>
                    <Heading size="md" mb={4} color="gray.900">Account Information</Heading>
                    <Stack gap={3}>
                        <Box>
                            <Text fontWeight="bold" color="gray.900">Email</Text>
                            <Text id="account-email" color="gray.700">{email}</Text>
                        </Box>
                        <Box>
                            <Text fontWeight="bold" color="gray.900">Name</Text>
                            <Text id="account-name" color="gray.700">{name}</Text>
                        </Box>
                        <Box>
                            <Text fontWeight="bold" color="gray.900">Available Tokens</Text>
                            <Text id="account-available-tokens" color="gray.700">{token}</Text>
                        </Box>
                        <Box>
                            <Text fontWeight="bold" color="gray.900">Role</Text>
                            <Text id="account-role" color="gray.700">{userRole}</Text>
                        </Box>
                    </Stack>
                </ContentCard>

                {userRole !== 'developer' && (
                    <ContentCard>
                        <Heading size="md" mb={4} color="gray.900">Developer Tools</Heading>
                        <Stack gap={4}>
                            <Text color="gray.700">Upgrade your account to access the API key dashboard and proxy testing tools.</Text>
                            {upgradeMessage && (
                                <Text color={upgradeMessageType === 'success' ? 'green.600' : 'red.600'}>
                                    {upgradeMessage}
                                </Text>
                            )}
                            <Button colorScheme="blue" size="lg" fontWeight="semibold" borderRadius="md" onClick={handleUpgradeToDeveloper} isDisabled={isUpgradingToDeveloper} width="full">
                                {isUpgradingToDeveloper ? 'Upgrading...' : 'Upgrade to Developer'}
                            </Button>
                        </Stack>
                    </ContentCard>
                )}

                <ContentCard>
                    <Heading size="md" mb={4} color="gray.900">Redeem Coupon</Heading>
                    <Stack gap={4}>
                        <Box>
                            <Text mb={2} color="gray.900">Coupon Code</Text>
                            <StyledInput value={redeemCode} onChange={(e) => setRedeemCode(e.target.value)} placeholder="Enter coupon code" />
                        </Box>
                        {redeemMessage && (
                            <Text color={redeemMessageType === 'success' ? 'green.600' : 'red.600'}>
                                {redeemMessage}
                            </Text>
                        )}
                        <Button colorScheme="blue" size="lg" fontWeight="semibold" borderRadius="md" onClick={handleRedeemCoupon} isDisabled={isRedeeming} width="full">
                            {isRedeeming ? 'Redeeming...' : 'Redeem'}
                        </Button>
                    </Stack>
                </ContentCard>

                <ContentCard>
                    <Heading size="md" mb={2} color="gray.900">Developer Access</Heading>
                    <Text color="gray.700" mb={4}>
                        Enable developer access to manage API keys and use advanced developer features. Enabling developer access includes a one-time 10,000 token grant.
                    </Text>
                    <Stack gap={4}>
                        {developerMessage && (
                            <Text color={developerMessageType === 'success' ? 'green.600' : 'red.600'}>
                                {developerMessage}
                            </Text>
                        )}
                        {developerAccessEnabled ? (
                            <Button as={Link} to="/api-keys" colorScheme="blue" size="lg" fontWeight="semibold" borderRadius="md" width="full">
                                Manage API Keys
                            </Button>
                        ) : (
                            <Button colorScheme="blue" size="lg" fontWeight="semibold" borderRadius="md" onClick={handleEnableDeveloperAccess} isDisabled={isEnablingDeveloperAccess} width="full">
                                {isEnablingDeveloperAccess ? 'Enabling...' : 'Enable Developer Access'}
                            </Button>
                        )}
                    </Stack>
                </ContentCard>

                <ContentCard>
                    <Heading size="md" mb={4} color="gray.900">Update Your Name</Heading>
                    <Stack gap={4}>
                        <Box>
                            <Text mb={2} color="gray.900">New Name</Text>
                            <StyledInput value={newName} onChange={(e) => setNewName(e.target.value)} placeholder="Enter your new name" />
                        </Box>
                        {nameMessage && (
                            <Text color={nameMessageType === 'success' ? 'green.600' : 'red.600'}>
                                {nameMessage}
                            </Text>
                        )}
                        <Button colorScheme="blue" size="lg" fontWeight="semibold" borderRadius="md" onClick={handleUpdateName} isDisabled={isUpdatingName} width="full">
                            {isUpdatingName ? 'Updating...' : 'Update Name'}
                        </Button>
                    </Stack>
                </ContentCard>

                <ContentCard>
                    <Heading size="md" mb={4} color="gray.900">Update Your Email</Heading>
                    <Stack gap={4}>
                        <Box>
                            <Text mb={2} color="gray.900">New Email Address</Text>
                            <StyledInput type="email" value={newEmail} onChange={(e) => setNewEmail(e.target.value)} placeholder="Enter your new email address" />
                        </Box>
                        <Box>
                            <Text mb={2} color="gray.900">Current Password</Text>
                            <StyledInput type="password" value={emailPassword} onChange={(e) => setEmailPassword(e.target.value)} placeholder="Enter your current password" />
                        </Box>
                        {emailMessage && (
                            <Text color={emailMessageType === 'success' ? 'green.600' : 'red.600'}>
                                {emailMessage}
                            </Text>
                        )}
                        <Button colorScheme="blue" size="lg" fontWeight="semibold" borderRadius="md" onClick={handleUpdateEmail} isDisabled={isUpdatingEmail} width="full">
                            {isUpdatingEmail ? 'Updating...' : 'Update Email'}
                        </Button>
                    </Stack>
                </ContentCard>

                <ContentCard>
                    <Heading size="md" mb={4} color="gray.900">Change Your Password</Heading>
                    <Stack gap={4}>
                        <Box>
                            <Text mb={2} color="gray.900">Current Password</Text>
                            <StyledInput type="password" value={currentPassword} onChange={(e) => setCurrentPassword(e.target.value)} placeholder="Enter your current password" />
                        </Box>
                        <Box>
                            <Text mb={2} color="gray.900">New Password</Text>
                            <StyledInput type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} placeholder="Enter your new password" />
                        </Box>
                        <Box>
                            <Text mb={2} color="gray.900">Confirm New Password</Text>
                            <StyledInput type="password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} placeholder="Confirm your new password" />
                        </Box>
                        {passwordMessage && (
                            <Text color={passwordMessageType === 'success' ? 'green.600' : 'red.600'}>
                                {passwordMessage}
                            </Text>
                        )}
                        <Button colorScheme="blue" size="lg" fontWeight="semibold" borderRadius="md" onClick={handleUpdatePassword} isDisabled={isUpdatingPassword} width="full">
                            {isUpdatingPassword ? 'Updating...' : 'Change Password'}
                        </Button>
                    </Stack>
                </ContentCard>
            </Stack>
        </Container>
    );
}
